import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Container, Row, Col, Card, Table, Form, Button, Alert, Badge } from 'react-bootstrap';
import axios from 'axios';
import { useAuth } from './auth/AuthContext';

export function VictorLudorumAdmin() {
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState('');
    const [savedMessage, setSavedMessage] = useState('');
    const [boatTypes, setBoatTypes] = useState([]);
    const [events, setEvents] = useState([]);

    const { getToken } = useAuth();

    const authConfig = useCallback(() => ({
        headers: { Authorization: `Bearer ${getToken()}` }
    }), [getToken]);

    const applyConfig = useCallback((data) => {
        setBoatTypes((data?.boatTypes || []).map(item => ({
            ...item,
            points: String(item.points ?? 0)
        })));
        setEvents((data?.events || []).map(item => ({
            ...item,
            overrideEnabled: item.overridePoints !== null && item.overridePoints !== undefined,
            overridePoints: item.overridePoints !== null && item.overridePoints !== undefined
                ? String(item.overridePoints)
                : ''
        })));
    }, []);

    const fetchData = useCallback(async () => {
        try {
            setLoading(true);
            setError('');
            const res = await axios.get('/api/victor-ludorum/config', authConfig());
            applyConfig(res.data);
        } catch (err) {
            console.error('Error loading Victor Ludorum config:', err);
            setError(err?.response?.data?.message || 'Failed to load Victor Ludorum configuration.');
        } finally {
            setLoading(false);
        }
    }, [authConfig, applyConfig]);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    const boatTypePointsById = useMemo(() => {
        const map = new Map();
        boatTypes.forEach(item => {
            const parsed = Number(item.points);
            map.set(item.boatTypeId, Number.isFinite(parsed) && parsed >= 0 ? Math.round(parsed) : 0);
        });
        return map;
    }, [boatTypes]);

    const overrideCount = useMemo(
        () => events.filter(event => event.overrideEnabled).length,
        [events]
    );

    function isValidPoints(value) {
        const parsed = Number(value);
        return String(value).trim() !== '' && Number.isFinite(parsed) && parsed >= 0;
    }

    const hasInvalidInput = useMemo(() => {
        if (boatTypes.some(item => !isValidPoints(item.points))) return true;
        return events.some(event => event.overrideEnabled && !isValidPoints(event.overridePoints));
    }, [boatTypes, events]);

    function updateBoatTypePoints(boatTypeId, value) {
        setSavedMessage('');
        setBoatTypes(prev => prev.map(item =>
            item.boatTypeId === boatTypeId ? { ...item, points: value } : item
        ));
    }

    function toggleEventOverride(eventId, enabled) {
        setSavedMessage('');
        setEvents(prev => prev.map(event => {
            if (event.id !== eventId) return event;
            return {
                ...event,
                overrideEnabled: enabled,
                overridePoints: enabled
                    ? (event.overridePoints !== '' ? event.overridePoints : String(boatTypePointsById.get(event.boatTypeId) ?? 0))
                    : ''
            };
        }));
    }

    function updateEventOverridePoints(eventId, value) {
        setSavedMessage('');
        setEvents(prev => prev.map(event =>
            event.id === eventId ? { ...event, overridePoints: value } : event
        ));
    }

    function effectivePointsFor(event) {
        if (event.overrideEnabled && isValidPoints(event.overridePoints)) {
            return Math.round(Number(event.overridePoints));
        }
        return boatTypePointsById.get(event.boatTypeId) ?? 0;
    }

    async function handleSave() {
        if (hasInvalidInput || saving) return;

        try {
            setSaving(true);
            setError('');
            setSavedMessage('');

            const payload = {
                boatTypePoints: boatTypes.map(item => ({
                    boatTypeId: item.boatTypeId,
                    points: Math.round(Number(item.points))
                })),
                eventOverrides: events
                    .filter(event => event.overrideEnabled)
                    .map(event => ({
                        eventId: event.id,
                        points: Math.round(Number(event.overridePoints))
                    }))
            };

            const res = await axios.put('/api/victor-ludorum/config', payload, authConfig());
            applyConfig(res.data);
            setSavedMessage('Victor Ludorum points saved.');
        } catch (err) {
            console.error('Error saving Victor Ludorum config:', err);
            setError(err?.response?.data?.message || 'Failed to save Victor Ludorum configuration.');
        } finally {
            setSaving(false);
        }
    }

    if (loading) {
        return <p><em>Loading...</em></p>;
    }

    return (
        <Container fluid className="pb-5 mb-5">
            <div className="d-flex align-items-center justify-content-between flex-wrap gap-2 mb-3">
                <h1 className="mb-0">Victor Ludorum</h1>
                <div className="d-flex align-items-center gap-2">
                    {savedMessage && <span className="text-success">{savedMessage}</span>}
                    <Button variant="primary" onClick={handleSave} disabled={saving || hasInvalidInput}>
                        {saving ? 'Saving...' : 'Save Changes'}
                    </Button>
                </div>
            </div>

            {error && <Alert variant="danger">{error}</Alert>}
            {hasInvalidInput && (
                <Alert variant="warning">
                    Points must be whole numbers of 0 or more.
                </Alert>
            )}

            <Row>
                <Col lg={4} className="mb-4">
                    <Card>
                        <Card.Header>
                            <strong>Points per Boat Type</strong>
                        </Card.Header>
                        <Card.Body className="p-0">
                            <Table hover responsive className="mb-0 align-middle">
                                <thead>
                                    <tr>
                                        <th>Boat Type</th>
                                        <th className="text-center">Events</th>
                                        <th style={{ width: 110 }}>Points</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {boatTypes.length === 0 && (
                                        <tr>
                                            <td colSpan={3} className="text-muted text-center py-3">
                                                No boat types in use for this meeting.
                                            </td>
                                        </tr>
                                    )}
                                    {boatTypes.map(item => (
                                        <tr key={item.boatTypeId}>
                                            <td>{item.label}</td>
                                            <td className="text-center">{item.eventCount}</td>
                                            <td>
                                                <Form.Control
                                                    type="number"
                                                    min={0}
                                                    step={1}
                                                    size="sm"
                                                    value={item.points}
                                                    isInvalid={!isValidPoints(item.points)}
                                                    onChange={(e) => updateBoatTypePoints(item.boatTypeId, e.target.value)}
                                                />
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </Table>
                        </Card.Body>
                    </Card>
                </Col>

                <Col lg={8} className="mb-4">
                    <Card>
                        <Card.Header className="d-flex align-items-center justify-content-between">
                            <strong>Event Overrides</strong>
                            <Badge bg={overrideCount > 0 ? 'info' : 'secondary'}>
                                {overrideCount} override{overrideCount === 1 ? '' : 's'}
                            </Badge>
                        </Card.Header>
                        <Card.Body className="p-0">
                            <Table hover responsive className="mb-0 align-middle">
                                <thead>
                                    <tr>
                                        <th>Event</th>
                                        <th>Boat Type</th>
                                        <th className="text-center">Boat Type Points</th>
                                        <th className="text-center" style={{ width: 100 }}>Override</th>
                                        <th style={{ width: 120 }}>Override Points</th>
                                        <th className="text-center">Effective Points</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {events.length === 0 && (
                                        <tr>
                                            <td colSpan={6} className="text-muted text-center py-3">
                                                No events found for this meeting.
                                            </td>
                                        </tr>
                                    )}
                                    {events.map(event => (
                                        <tr key={event.id}>
                                            <td>
                                                {event.title}
                                                {event.eventCode && (
                                                    <span className="text-muted ms-2" style={{ fontSize: 12 }}>{event.eventCode}</span>
                                                )}
                                            </td>
                                            <td>{event.boatTypeLabel || '-'}</td>
                                            <td className="text-center">{boatTypePointsById.get(event.boatTypeId) ?? 0}</td>
                                            <td className="text-center">
                                                <Form.Check
                                                    type="switch"
                                                    id={`vl-override-${event.id}`}
                                                    checked={event.overrideEnabled}
                                                    onChange={(e) => toggleEventOverride(event.id, e.target.checked)}
                                                />
                                            </td>
                                            <td>
                                                {event.overrideEnabled ? (
                                                    <Form.Control
                                                        type="number"
                                                        min={0}
                                                        step={1}
                                                        size="sm"
                                                        value={event.overridePoints}
                                                        isInvalid={!isValidPoints(event.overridePoints)}
                                                        onChange={(e) => updateEventOverridePoints(event.id, e.target.value)}
                                                    />
                                                ) : (
                                                    <span className="text-muted">-</span>
                                                )}
                                            </td>
                                            <td className="text-center">
                                                <strong>{effectivePointsFor(event)}</strong>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </Table>
                        </Card.Body>
                    </Card>
                </Col>
            </Row>
        </Container>
    );
}
